import { Bot, MessageSquare, Zap, Info } from "lucide-react";
import { DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/MultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: Partial<GuildConfig>) => void;
}

export function AiHubTab({ config, channels, onChange }: Props) {
  const faq = config.ai_faq || { enabled: false, knowledge_base: "", allowed_channel_ids: [], trigger_mode: "mention", reply_style: "embed" };

  const updateFaq = <K extends keyof typeof faq>(k: K, v: (typeof faq)[K]) => {
    onChange({ ai_faq: { ...faq, [k]: v } });
  };

  return (
    <div className="space-y-6">
      {/* AI FAQ Section */}
      <Section
        icon={MessageSquare}
        title="Global AI FAQ"
        description="Automatically answers questions in your server channels using AI and your knowledge base."
        enabled={faq.enabled}
        onToggle={(v) => updateFaq("enabled", v)}
      >
        <div className="space-y-4">
          <Field 
            label="Knowledge Base" 
            hint="The source of truth for the AI. Include rules, FAQ, info, etc."
          >
            <Textarea
              value={faq.knowledge_base}
              rows={8}
              placeholder="Example: We are a FiveM server. Whitelist is open at #apply. Rules: 1. No RDM..."
              onChange={(e) => updateFaq("knowledge_base", e.target.value)}
              className="bg-background min-h-[150px]"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field 
              label="Allowed Channels" 
              hint="AI will only listen and reply in these channels."
            >
              <MultiSelect
                options={channels.filter(c => c.type === 0).map(c => ({ id: c.id, name: c.name }))}
                value={faq.allowed_channel_ids}
                onChange={(v) => updateFaq("allowed_channel_ids", v)}
                prefix="#"
              />
            </Field>
            <Field 
              label="Trigger Mode" 
              hint="Mention = AI only replies when tagged. All Messages = AI reads everything."
            >
              <Select
                value={faq.trigger_mode}
                onValueChange={(v) => updateFaq("trigger_mode", v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mention">On Mention (@Bot)</SelectItem>
                  <SelectItem value="all">Every Message (Auto-detect questions)</SelectItem>
                  <SelectItem value="questions">Questions Only (Experimental)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* AI Ask Command Callout */}
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 flex gap-3 mt-2">
            <Zap className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <strong>🤖 AI Chat command enabled:</strong> Members can use the <code className="text-primary font-mono text-[11px]">/ask [question]</code> slash command anywhere on your server. Areon AI will reply using the <strong>Knowledge Base</strong> configured above!
            </div>
          </div>
        </div>
      </Section>

      {/* Custom Bot / White-Label Section — temporarily unavailable */}
      <Section
        icon={Bot}
        title="Custom Bot (White-Label)"
        description="Run Areon under your own Bot name, avatar, and token. Professional branding for your community."
        enabled={false}
        onToggle={() => {}}
        disabled
      >
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 flex gap-3">
          <Info className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200/80 leading-relaxed">
            <strong>Temporarily unavailable.</strong> We're improving this feature and it's paused for new setups
            right now. Keep an eye on our Discord announcements for when it's back.
          </div>
        </div>
      </Section>
    </div>
  );
}
